import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { translations } from '../translations';

const LanguageContext = createContext();

// Detect browser language
function detectBrowserLanguage() {
  const browserLang = navigator.language || navigator.userLanguage;
  // Check if browser language starts with 'es' (Spanish)
  if (browserLang.toLowerCase().startsWith('es')) {
    return 'es';
  }
  // Default to Russian for other languages
  return 'ru';
}

export function LanguageProvider({ children }) {
  const location = useLocation();
  const params = useParams();
  
  // Initialize language state
  const [lang, setLangState] = useState(() => {
    // First try URL parameter
    if (params.lang && ['ru', 'es'].includes(params.lang)) {
      return params.lang;
    }
    // Then try localStorage
    const saved = localStorage.getItem('language');
    if (saved && ['ru', 'es'].includes(saved)) {
      return saved;
    }
    // Finally use browser language
    return detectBrowserLanguage();
  });

  const t = translations[lang];

  // Update localStorage and URL when language changes
  const setLang = (newLang) => {
    if (!['ru', 'es'].includes(newLang)) return;
    
    setLangState(newLang);
    localStorage.setItem('language', newLang);
    
    // Update URL with new language using window.history to avoid component remount
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(ru|es)/, '');
    const newPath = `/${newLang}${pathWithoutLang || ''}`;
    window.history.replaceState(null, '', newPath);
  };

  // Sync language with URL parameter on route changes
  useEffect(() => {
    if (params.lang && ['ru', 'es'].includes(params.lang)) {
      if (params.lang !== lang) {
        setLangState(params.lang);
        localStorage.setItem('language', params.lang);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.lang]); // Only depend on params.lang to avoid infinite loops

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}
